'use client';

import React, { useRef, useEffect, useState, useLayoutEffect } from 'react';
import Image from 'next/image';
import { useInView } from '@/lib/useInView';
import CursorTooltip from '@/components/ui/CursorTooltip';

type LayoutWhatWeDoCarouselDesktopProps = {
  images: string[];
  widths?: number[]; // Explicit widths for each image/video (at 500px height)
  altTexts?: string[]; // Alt text for each image/video
};

/**
 * LayoutWhatWeDoCarouselDesktop - Desktop-only carousel component for the "Join our team" section.
 * 
 * Raw JavaScript implementation for better control over infinite scroll.
 * Note: This carousel is desktop-only (768px and above). Images have variable sizes,
 * so we maintain fixed height (500px) with auto width. The heading and CTA button are
 * handled by the parent WhatWeDoCarousel component.
 * 
 * @component
 */
export default function LayoutWhatWeDoCarouselDesktop({ images, widths, altTexts }: LayoutWhatWeDoCarouselDesktopProps) {
  const { ref } = useInView<HTMLDivElement>();
  const containerRef = useRef<HTMLDivElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const hasDraggedRef = useRef(false);
  const isDraggingRef = useRef(false);
  const isHoveredRef = useRef(false);
  const [isHovered, setIsHovered] = useState(false); // State to trigger animation re-evaluation
  const [isDragging, setIsDragging] = useState(false); // Only for cursor styling
  const pendingDragStart = useRef<{ clientX: number; translate: number } | null>(null);
  const [translateX, setTranslateX] = useState(0);
  const [widthCalculated, setWidthCalculated] = useState(false);
  const translateXRef = useRef(0);
  const dragStartX = useRef(0);
  const dragStartTranslate = useRef(0);
  const animationFrameId = useRef<number | null>(null);
  const setWidthRef = useRef<number | null>(null);
  const [isTouchDevice, setIsTouchDevice] = useState(false);
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);

  // Check for reduced motion preference
  useEffect(() => {
    const checkReducedMotion = () => {
      const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      setPrefersReducedMotion(mediaQuery.matches);
    };
    
    checkReducedMotion(); // Initial check
    
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    const handleChange = () => {
      setPrefersReducedMotion(mediaQuery.matches);
    };
    
    // Listen for changes (some browsers support this)
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleChange);
      return () => mediaQuery.removeEventListener('change', handleChange);
    } else {
      // Fallback for older browsers
      mediaQuery.addListener(handleChange);
      return () => mediaQuery.removeListener(handleChange);
    }
  }, []);

  // Detect touch device
  useEffect(() => {
    const checkTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
    setIsTouchDevice(checkTouch);
  }, []);
  
  // Duplicate images for seamless infinite scroll
  const duplicatedImages = [...images, ...images];

  // Get spacing based on viewport width
  const getSpacing = () => {
    return 24; // 24px margin between cards on desktop
  };

  // Calculate set width using explicit widths if provided, otherwise measure from DOM
  useEffect(() => {
    if (!wrapperRef.current || setWidthRef.current !== null) return;

    // If we have explicit widths, calculate immediately
    if (widths && widths.length === images.length) {
      const calculateFromWidths = () => {
        const spacing = getSpacing();
        let totalWidth = 0;
        
        for (let i = 0; i < widths.length; i++) {
          totalWidth += widths[i] + spacing;
        }
        
        setWidthRef.current = totalWidth;
        setWidthCalculated(true);
      };
      
      calculateFromWidths();
      
      // Recalculate on resize to account for responsive spacing
      const handleResize = () => {
        if (setWidthRef.current !== null) {
          calculateFromWidths();
        }
      };
      
      window.addEventListener('resize', handleResize);
      return () => window.removeEventListener('resize', handleResize);
    }

    // Otherwise, measure from DOM (fallback)
    const calculateWidth = () => {
      const wrapper = wrapperRef.current;
      if (!wrapper) return false;

      void wrapper.offsetHeight; // Force layout recalculation

      let totalWidth = 0;
      let validSlides = 0;

      for (let i = 0; i < images.length; i++) {
        const slide = wrapper.children[i] as HTMLElement;
        if (!slide) continue;

        const slideWidth = slide.offsetWidth;
        if (slideWidth === 0) return false;

        const computedStyle = window.getComputedStyle(slide);
        const marginRight = parseFloat(computedStyle.marginRight) || 0;

        totalWidth += slideWidth + marginRight;
        validSlides++;
      }

      if (validSlides === images.length && totalWidth > 0) {
        setWidthRef.current = totalWidth;
        setWidthCalculated(true);
        return true;
      }
      return false;
    };

    if (calculateWidth()) return;

    const timeout = setTimeout(() => calculateWidth(), 300);
    return () => clearTimeout(timeout);
  }, [images.length, widths]);

  // Continuous scroll animation (time-based for smooth performance)
  useEffect(() => {
    // Don't animate if user prefers reduced motion
    if (prefersReducedMotion) {
      if (animationFrameId.current) {
        cancelAnimationFrame(animationFrameId.current);
        animationFrameId.current = null;
      }
      return;
    }
    
    // Don't start animation until width is calculated
    // For non-touch devices: pause on hover instead of on drag
    // For touch devices: keep original behavior (stop on drag)
    const shouldPause = isTouchDevice 
      ? (isDraggingRef.current || hasDraggedRef.current)
      : (isDraggingRef.current || hasDraggedRef.current || isHoveredRef.current);
    
    if (!wrapperRef.current || !widthCalculated || !setWidthRef.current || shouldPause) {
      if (animationFrameId.current) {
        cancelAnimationFrame(animationFrameId.current);
        animationFrameId.current = null;
      }
      return;
    }

    const scrollSpeed = 60; // pixels per second
    const setWidth = setWidthRef.current; // Cache the width
    let lastTime: number | null = null;
    let x = translateXRef.current;

    const animate = (timestamp: number) => {
      const shouldPauseNow = isTouchDevice 
        ? (isDraggingRef.current || hasDraggedRef.current)
        : (isDraggingRef.current || hasDraggedRef.current || isHoveredRef.current);
      
      if (!wrapperRef.current || shouldPauseNow) {
        if (animationFrameId.current) {
          cancelAnimationFrame(animationFrameId.current);
          animationFrameId.current = null;
        }
        return;
      }

      if (lastTime !== null) {
        // Calculate delta time in seconds
        const deltaSeconds = (timestamp - lastTime) / 1000;

        // Move left at `speed` px/sec, independent of FPS
        x -= scrollSpeed * deltaSeconds;

        // If we've scrolled the whole set off-screen, wrap around
        if (x <= -setWidth) {
          x += setWidth;
        }

        // Use translate3d for GPU acceleration
        wrapperRef.current.style.transform = `translate3d(${x}px, 0, 0)`;
        translateXRef.current = x;
        // Don't call setTranslateX here - it causes unnecessary re-renders
        // Only update ref and DOM directly for smooth animation
      }

      lastTime = timestamp;
      animationFrameId.current = requestAnimationFrame(animate);
    };

    // Start animation
    animationFrameId.current = requestAnimationFrame(animate);

    return () => {
      if (animationFrameId.current) {
        cancelAnimationFrame(animationFrameId.current);
        animationFrameId.current = null;
      }
    };
  }, [widthCalculated, isTouchDevice, isHovered, prefersReducedMotion]);

  // Lock transform before re-render when drag starts
  useLayoutEffect(() => {
    if (pendingDragStart.current && wrapperRef.current) {
      const { translate } = pendingDragStart.current;
      wrapperRef.current.style.transform = `translate3d(${translate}px, 0, 0)`;
      translateXRef.current = translate;
      pendingDragStart.current = null;
    }
  });

  // Hover handlers (non-touch devices only)
  const handleMouseEnter = () => {
    if (!isTouchDevice) {
      isHoveredRef.current = true;
      setIsHovered(true); // Update state to trigger animation re-evaluation
    }
  };

  const handleMouseLeave = () => {
    if (!isTouchDevice) {
      isHoveredRef.current = false;
      setIsHovered(false); // Update state to trigger animation re-evaluation
    }
  };

  // Drag handlers
  const handleDragStart = (e: React.MouseEvent | React.TouchEvent) => {
    // Prevent default image dragging behavior
    e.preventDefault();
    
    // Stop animation first to prevent race conditions
    if (animationFrameId.current) {
      cancelAnimationFrame(animationFrameId.current);
      animationFrameId.current = null;
    }
    
    // CRITICAL: Read transform BEFORE any state changes
    let currentTranslate = translateXRef.current;
    if (wrapperRef.current) {
      const style = window.getComputedStyle(wrapperRef.current);
      const transform = style.transform;
      if (transform && transform !== 'none') {
        const matrix = new DOMMatrix(transform);
        currentTranslate = matrix.m41; // m41 is translateX
        translateXRef.current = currentTranslate;
      }
    }
    
    const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
    dragStartX.current = clientX;
    dragStartTranslate.current = currentTranslate;
    
    // Store pending drag start to restore transform after re-render
    pendingDragStart.current = { clientX, translate: currentTranslate };
    
    // Update refs (no re-render) and only update cursor state
    // For non-touch: don't set hasDraggedRef (hover handles pausing, drag just pauses during drag)
    // For touch: set hasDraggedRef to stop animation permanently
    if (isTouchDevice) {
      hasDraggedRef.current = true;
    }
    isDraggingRef.current = true;
    setIsDragging(true); // Only for cursor styling, minimal re-render
    
    // Hide tooltip with CSS to avoid re-renders
    // The tooltip is portaled to document.body, so we find it there
    const tooltip = document.body.querySelector('[role="tooltip"]') as HTMLElement;
    if (tooltip) {
      tooltip.style.display = 'none';
    }
  };

  const handleDragMove = (e: MouseEvent | TouchEvent) => {
    if (!isDraggingRef.current || !wrapperRef.current) return;

    // Prevent default scrolling behavior
    e.preventDefault();

    const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
    const deltaX = clientX - dragStartX.current;
    let newTranslate = dragStartTranslate.current + deltaX;

    // Use pre-calculated set width (should be set at initialization)
    // If not set, we can't do infinite scroll, so just allow normal dragging

    // Handle infinite scroll when dragging
    if (setWidthRef.current && setWidthRef.current > 0) {
      // If dragging left past the start of first set, wrap to duplicate set
      if (newTranslate > 0) {
        newTranslate = newTranslate - setWidthRef.current;
        // Update drag start position to maintain smooth dragging
        dragStartTranslate.current = newTranslate;
        dragStartX.current = clientX;
      }
      // If dragging right past the end of duplicate set, wrap to first set
      if (newTranslate < -setWidthRef.current) {
        newTranslate = newTranslate + setWidthRef.current;
        // Update drag start position to maintain smooth dragging
        dragStartTranslate.current = newTranslate;
        dragStartX.current = clientX;
      }
    }

    // Use translate3d for GPU acceleration
    if (wrapperRef.current) {
      wrapperRef.current.style.transform = `translate3d(${newTranslate}px, 0, 0)`;
    }
    translateXRef.current = newTranslate;
    // Don't update state during drag - it causes re-renders and jumps
    // State is only needed for React tracking, but we update DOM directly
  };

  const handleDragEnd = () => {
    isDraggingRef.current = false;
    setIsDragging(false); // Only for cursor styling
    // For non-touch: animation will resume if not hovered (hover state controls it)
    // For touch: animation will remain stopped since hasDraggedRef.current is true
  };

  // Add global drag move and end listeners
  useEffect(() => {
    // Use a ref check function that always reads current value
    const checkIsDragging = () => isDraggingRef.current;
    
    if (checkIsDragging()) {
      const handleMove = (e: MouseEvent | TouchEvent) => {
        if (checkIsDragging()) {
          handleDragMove(e);
        }
      };
      const handleEnd = () => {
        if (checkIsDragging()) {
          handleDragEnd();
        }
        document.removeEventListener('mousemove', handleMove);
        document.removeEventListener('mouseup', handleEnd);
        document.removeEventListener('touchmove', handleMove);
        document.removeEventListener('touchend', handleEnd);
      };

      document.addEventListener('mousemove', handleMove);
      document.addEventListener('mouseup', handleEnd);
      document.addEventListener('touchmove', handleMove, { passive: false });
      document.addEventListener('touchend', handleEnd);

      return () => {
        document.removeEventListener('mousemove', handleMove);
        document.removeEventListener('mouseup', handleEnd);
        document.removeEventListener('touchmove', handleMove);
        document.removeEventListener('touchend', handleEnd);
      };
    }
  }, [isDragging]); // Only depend on cursor state, not drag refs

  return (
    <>
      {/* Desktop-only carousel container (hidden below 768px) */}
      <div className="w-full hidden md:flex justify-center">
        <div className="w-full max-w-[1920px]">
          <div className="w-full">
            <div 
              ref={ref} 
              className="w-full overflow-hidden"
            >
              {!isTouchDevice ? (
                <CursorTooltip tooltipText="Drag" backgroundColor="#FACC15" textColor="#000000">
                  <div
                    ref={containerRef}
                    className="w-full overflow-hidden desktop-carousel-tooltip-container"
                    onMouseDown={handleDragStart}
                    onMouseEnter={handleMouseEnter}
                    onMouseLeave={handleMouseLeave}
                    onTouchStart={handleDragStart}
                    style={{ 
                      cursor: isDragging ? 'grabbing' : 'grab',
                      userSelect: 'none',
                      WebkitUserSelect: 'none',
                      touchAction: 'pan-y'
                    }}
                  >
                    <div
                      ref={wrapperRef}
                      className="flex desktop-carousel-wrapper"
                      style={{
                        willChange: 'transform',
                        gap: 0,
                        margin: 0,
                        padding: 0,
                      }}
                    >
                      {duplicatedImages.map((image, index) => {
                        const isVideo = image.endsWith('.mp4') || image.endsWith('.webm') || image.endsWith('.mov');
                        const originalIndex = index % images.length;
                        const explicitWidth = widths && widths[originalIndex] ? widths[originalIndex] : null;
                        const spacing = getSpacing();
                        const altText = altTexts && altTexts[originalIndex] ? altTexts[originalIndex] : `Carousel image ${originalIndex + 1}`;
                      
                      return (
                        <div
                          key={index}
                          className="flex-shrink-0 desktop-carousel-slide"
                          style={{ 
                            pointerEvents: 'none',
                            userSelect: 'none',
                            WebkitUserSelect: 'none',
                            width: explicitWidth ? `${explicitWidth}px` : 'auto',
                            padding: 0,
                            marginRight: `${spacing}px`,
                          }}
                        >
                          <div className="relative" style={{ width: explicitWidth ? `${explicitWidth}px` : 'auto', height: '500px' }}>
                            <div className="relative overflow-hidden rounded-3xl desktop-carousel-image-container" style={{ width: '100%', height: '100%' }}>
                              {isVideo ? (
                                <video
                                  src={image}
                                  autoPlay
                                  loop
                                  muted
                                  playsInline
                                  preload="auto"
                                  className="rounded-3xl desktop-carousel-image"
                                  style={{
                                    height: '500px',
                                    width: explicitWidth ? `${explicitWidth}px` : 'auto',
                                    objectFit: 'cover',
                                    display: 'block',
                                  }}
                                  draggable={false}
                                  aria-label={altText}
                                />
                              ) : (
                                <Image
                                  src={image}
                                  alt={altText}
                                  width={explicitWidth || 400}
                                  height={500}
                                  sizes="(max-width: 1024px) 40vw, 30vw"
                                  className="rounded-3xl desktop-carousel-image"
                                  style={{
                                    height: '500px',
                                    width: explicitWidth ? `${explicitWidth}px` : 'auto',
                                    objectFit: 'cover',
                                  }}
                                  draggable={false}
                                />
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
                </CursorTooltip>
              ) : (
                <div
                  ref={containerRef}
                  className="w-full overflow-hidden"
                  onMouseDown={handleDragStart}
                  onMouseEnter={handleMouseEnter}
                  onMouseLeave={handleMouseLeave}
                  onTouchStart={handleDragStart}
                  style={{ 
                    cursor: isDragging ? 'grabbing' : 'grab',
                    userSelect: 'none',
                    WebkitUserSelect: 'none',
                    touchAction: 'pan-y'
                  }}
                >
                  <div
                    ref={wrapperRef}
                    className="flex desktop-carousel-wrapper"
                    style={{
                      willChange: 'transform',
                      gap: 0,
                      margin: 0,
                      padding: 0,
                    }}
                  >
                    {duplicatedImages.map((image, index) => {
                      const isVideo = image.endsWith('.mp4') || image.endsWith('.webm') || image.endsWith('.mov');
                      const originalIndex = index % images.length;
                      const explicitWidth = widths && widths[originalIndex] ? widths[originalIndex] : null;
                      const spacing = getSpacing();
                      const altText = altTexts && altTexts[originalIndex] ? altTexts[originalIndex] : `Carousel image ${originalIndex + 1}`;
                      
                      return (
                        <div
                          key={index}
                          className="flex-shrink-0 desktop-carousel-slide"
                          style={{ 
                            pointerEvents: 'none',
                            userSelect: 'none',
                            WebkitUserSelect: 'none',
                            width: explicitWidth ? `${explicitWidth}px` : 'auto',
                            marginRight: `${spacing}px`
                          }}
                        >
                          <div className="relative" style={{ width: explicitWidth ? `${explicitWidth}px` : 'auto', height: '500px' }}>
                            <div className="relative overflow-hidden rounded-3xl desktop-carousel-image-container" style={{ width: '100%', height: '100%' }}>
                              {isVideo ? (
                                <video
                                  src={image}
                                  autoPlay
                                  loop
                                  muted
                                  playsInline
                                  preload="auto"
                                  className="rounded-3xl desktop-carousel-image"
                                  style={{
                                    height: '500px',
                                    width: explicitWidth ? `${explicitWidth}px` : 'auto',
                                    objectFit: 'cover',
                                    display: 'block',
                                  }}
                                  draggable={false}
                                  aria-label={altText}
                                />
                              ) : (
                                <Image
                                  src={image}
                                  alt={altText}
                                  width={explicitWidth || 400}
                                  height={500}
                                  sizes="(max-width: 1024px) 40vw, 30vw"
                                  className="rounded-3xl desktop-carousel-image"
                                  style={{
                                    height: '500px',
                                    width: explicitWidth ? `${explicitWidth}px` : 'auto',
                                    objectFit: 'cover',
                                  }}
                                  draggable={false}
                                />
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <style jsx global>{`
        .desktop-carousel-wrapper {
          will-change: transform;
          backface-visibility: hidden;
          -webkit-backface-visibility: hidden;
        }

        .desktop-carousel-slide {
          width: auto;
          flex-shrink: 0;
          /* iOS Safari fixes for variable image sizes */
          will-change: transform;
          backface-visibility: hidden;
          -webkit-backface-visibility: hidden;
          transform: translateZ(0);
          -webkit-transform: translateZ(0);
        }

        .desktop-carousel-image-container {
          /* Container uses explicit width from props */
          display: block;
          height: 31.25rem;
          width: 100%;
        }

        .desktop-carousel-image {
          /* Fixed height, explicit width from carouselData, object-fit cover */
          height: 31.25rem !important;
          object-fit: cover !important;
          max-width: none;
          /* iOS Safari fixes */
          will-change: transform;
          backface-visibility: hidden;
          -webkit-backface-visibility: hidden;
          display: block;
        }

        /* Spacing between slides - now handled inline via style prop */
        /* No CSS margin rules - using inline styles only */
      `}</style>
    </>
  );
}
